import { api } from './client';
import type { Page } from './catalogs';

// ==================== Advance Payment ====================

export type SourceDocType = 'bank_receipt' | 'cash_receipt';

export interface AdvancePayment {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  source_doc_type: SourceDocType;
  source_doc_id: string;
  contractor_id: string;
  contract_id: string | null;
  advance_amount: string;
  vat_rate: string;
  vat_amount: string;
  generated_tax_invoice_id: string | null;
  base_order_id?: string | null;
  items?: (AdvancePaymentItem & { id: string; line_number: number })[];
  created_at: string;
  updated_at: string;
  external_source?: string | null;
}

export interface AdvancePaymentItem {
  nomenclature_id?: string | null;
  description: string;
  ukt_zed_code?: string | null;
  quantity: string;
  price_without_vat: string;
  vat_rate_id: string;
  base_amount: string;
  vat_amount: string;
}

export interface AdvancePaymentCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  comment?: string | null;
  source_doc_type: SourceDocType;
  source_doc_id: string;
  contractor_id: string;
  contract_id?: string | null;
  advance_amount: string;
  vat_rate?: string;
  // 2026-08-05: рахунок покупця — джерело товарних рядків авансової ПН.
  base_order_id?: string | null;
  // 0400: ТЧ — рядки майбутньої авансової ПН.
  items?: AdvancePaymentItem[];
}

export interface AdvanceSuggestion {
  suggests_advance: boolean;
  reason: string;
  contractor_balance: number;
  amount: number;
  /** ч.1446: авансова частина надходження (решта гасить борг). */
  advance_amount?: number;
  registration_id?: string | null;  // ч.907: уже створена реєстрація за надходженням
}

const ADV_BASE = '/documents/advance-payment-registrations';

export const advancePayments = {
  // Авто-заповнення ТЧ з рахунку (пропорційно сумі авансу, серверна логіка).
  linesFromOrder: (p: { organization_id: string; base_order_id: string;
    advance_amount: string }) =>
    api.post<{ items: AdvancePaymentItem[] }>(
      `${ADV_BASE}/lines-from-order`, p).then((r) => r.data),
  list: (params?: { organization_id?: string; posted?: boolean;
    date_from?: string; date_to?: string;
    sort_by?: string; sort_dir?: 'asc' | 'desc'; limit?: number; offset?: number }) =>
    api.get<Page<AdvancePayment>>(ADV_BASE, { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<AdvancePayment>(`${ADV_BASE}/${id}`).then((r) => r.data),
  create: (payload: AdvancePaymentCreate) =>
    api.post<AdvancePayment>(ADV_BASE, payload).then((r) => r.data),
  update: (id: string, payload: Partial<AdvancePaymentCreate>) =>
    api.patch<AdvancePayment>(`${ADV_BASE}/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`${ADV_BASE}/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<AdvancePayment>(`${ADV_BASE}/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<AdvancePayment>(`${ADV_BASE}/${id}/unpost`).then((r) => r.data),
  suggestsForBankReceipt: (bankReceiptId: string) =>
    api.get<AdvanceSuggestion>(`${ADV_BASE}/suggests/${bankReceiptId}`).then((r) => r.data),
};

// ==================== Appendix 1 ====================

export interface Appendix1Item {
  id: string;
  line_number: number;
  nomenclature_id: string;
  characteristic_id: string | null;
  uom_id: string | null;
  quantity: string;
  unit_price: string;
  base_amount: string;
  vat_rate: string;
  vat_amount: string;
}

export interface Appendix1 {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  deletion_mark: boolean;
  parent_tax_invoice_id: string;
  total_items_count: number;
  total_amount: string;
  total_vat: string;
  items: Appendix1Item[];
  created_at: string;
  updated_at: string;
  posted_at: string | null;
  external_source?: string | null;
}

const APP1_BASE = '/documents/tax-invoice-appendix-1';

export const appendix1 = {
  list: (params?: { organization_id?: string; parent_tax_invoice_id?: string; limit?: number }) =>
    api.get<Page<Appendix1>>(APP1_BASE, { params }).then((r) => r.data),
  get: (id: string) => api.get<Appendix1>(`${APP1_BASE}/${id}`).then((r) => r.data),
  post: (id: string) => api.post<Appendix1>(`${APP1_BASE}/${id}/post`).then((r) => r.data),
  unpost: (id: string) => api.post<Appendix1>(`${APP1_BASE}/${id}/unpost`).then((r) => r.data),
};

// ==================== Appendix 2 ====================

export type Appendix2Subtype =
  | 'WARRANTY_REPAIR' | 'CONSTRUCTION' | 'EQUIPMENT_INSTALLATION' | 'OTHER';

export interface Appendix2Item {
  id?: string;
  line_number: number;
  nomenclature_id?: string | null;
  description?: string | null;
  uom_id?: string | null;
  quantity: string;
  unit_price: string;
  vat_rate?: string;
  base_amount?: string;
  vat_amount?: string;
}

export interface Appendix2 {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  deletion_mark: boolean;
  parent_tax_invoice_id: string;
  operation_subtype: Appendix2Subtype;
  total_items_count: number;
  total_amount: string;
  total_vat: string;
  items: Appendix2Item[];
  created_at: string;
  updated_at: string;
  posted_at: string | null;
  external_source?: string | null;
}

export interface Appendix2Create {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  parent_tax_invoice_id: string;
  operation_subtype: Appendix2Subtype;
  items: Appendix2Item[];
}

const APP2_BASE = '/documents/tax-invoice-appendix-2';

export const appendix2 = {
  list: (params?: { organization_id?: string; parent_tax_invoice_id?: string; operation_subtype?: Appendix2Subtype; limit?: number }) =>
    api.get<Page<Appendix2>>(APP2_BASE, { params }).then((r) => r.data),
  get: (id: string) => api.get<Appendix2>(`${APP2_BASE}/${id}`).then((r) => r.data),
  create: (payload: Appendix2Create) => api.post<Appendix2>(APP2_BASE, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`${APP2_BASE}/${id}`).then(() => undefined),
  post: (id: string) => api.post<Appendix2>(`${APP2_BASE}/${id}/post`).then((r) => r.data),
  unpost: (id: string) => api.post<Appendix2>(`${APP2_BASE}/${id}/unpost`).then((r) => r.data),
};

// ==================== Proportional VAT Recalculation ====================

export interface PropRecalc {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  deletion_mark: boolean;
  comment: string | null;
  year: number;
  recalculation_date: string;
  total_taxable_supplies: string;
  total_exempt_supplies: string;
  coefficient_actual: string;
  coefficient_used_during_year: string;
  total_general_vat_credit: string;
  difference_amount: string;
  calculation_details: Record<string, unknown> | null;
  posted_at: string | null;
  created_at: string;
  updated_at: string;
  external_source?: string | null;
}

export interface PropRecalcCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  comment?: string | null;
  year: number;
  recalculation_date: string;
  total_taxable_supplies?: string;
  total_exempt_supplies?: string;
  coefficient_used_during_year: string;
  total_general_vat_credit?: string;
}

export interface PropRecalcCalculateResponse {
  total_supplies: string;
  posted_sales_count: number;
  suggestion: string;
  taxable_supplies: string;
  exempt_supplies: string;
  non_business_supplies: string;
  not_object_supplies: string;
  export_supplies: string;
  unclassified_supplies: string;
  requires_manual_split: boolean;
}

const PR_BASE = '/documents/proportional-vat-recalculation';

export const propRecalc = {
  list: (params?: { organization_id?: string; year?: number;
    sort_by?: string; sort_dir?: 'asc' | 'desc'; limit?: number; offset?: number }) =>
    api.get<Page<PropRecalc>>(PR_BASE, { params }).then((r) => r.data),
  get: (id: string) => api.get<PropRecalc>(`${PR_BASE}/${id}`).then((r) => r.data),
  create: (payload: PropRecalcCreate) => api.post<PropRecalc>(PR_BASE, payload).then((r) => r.data),
  update: (id: string, payload: Record<string, unknown>) =>
    api.patch<PropRecalc>(`${PR_BASE}/${id}`, payload).then((r) => r.data),
  post: (id: string) => api.post<PropRecalc>(`${PR_BASE}/${id}/post`).then((r) => r.data),
  unpost: (id: string) => api.post<PropRecalc>(`${PR_BASE}/${id}/unpost`).then((r) => r.data),
  remove: (id: string) => api.delete(`${PR_BASE}/${id}`).then(() => undefined),
  /** Етап 2б: зведені ПН року — кандидати для РК. */
  compiledPns: (id: string) =>
    api.get<{ items: Array<{ id: string; invoice_number: string; invoice_date: string;
      total_amount: string; erpn_status: string }> }>(
      `${PR_BASE}/${id}/compiled-pns`).then((r) => r.data),
  createRk: (id: string, compiled_pn_id: string) =>
    api.post<{ id: string; invoice_number: string; base_delta: string; vat_delta: string }>(
      `${PR_BASE}/${id}/create-rk`, { compiled_pn_id }).then((r) => r.data),
  calculateFromYear: (organization_id: string, year: number) =>
    api.post<PropRecalcCalculateResponse>(`${PR_BASE}/calculate-from-year`, {
      organization_id, year,
    }).then((r) => r.data),
};

// ==================== Proportional VAT Coefficient ====================

export interface PropCoef {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  deletion_mark: boolean;
  comment: string | null;
  year: number;
  coefficient: string;
  based_on_year: number;
  based_on_recalculation_id: string | null;
  approval_date: string;
  approved_by_id: string | null;
  posted_at: string | null;
  created_at: string;
  updated_at: string;
  external_source?: string | null;
}

export interface PropCoefCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  comment?: string | null;
  year: number;
  coefficient: string;
  based_on_year: number;
  based_on_recalculation_id?: string | null;
  approval_date?: string | null;
}

const PC_BASE = '/documents/proportional-vat-coefficient';

export const propCoef = {
  list: (params?: { organization_id?: string; year?: number;
    sort_by?: string; sort_dir?: 'asc' | 'desc'; limit?: number; offset?: number }) =>
    api.get<Page<PropCoef>>(PC_BASE, { params }).then((r) => r.data),
  get: (id: string) => api.get<PropCoef>(`${PC_BASE}/${id}`).then((r) => r.data),
  create: (payload: PropCoefCreate) => api.post<PropCoef>(PC_BASE, payload).then((r) => r.data),
  update: (id: string, payload: Record<string, unknown>) =>
    api.patch<PropCoef>(`${PC_BASE}/${id}`, payload).then((r) => r.data),
  /** Етап 2а (2026-08-09): зведена ПН за ст.199.1 на місяць. */
  createCompiledPn: (id: string, p: {
    month: number; year: number; base_amount: string; not_issued_reason?: string;
  }) => api.post<{ id: string; invoice_number: string; base_amount: string; vat_amount: string }>(
    `${PC_BASE}/${id}/create-compiled-pn`, p).then((r) => r.data),
  post: (id: string) => api.post<PropCoef>(`${PC_BASE}/${id}/post`).then((r) => r.data),
  unpost: (id: string) => api.post<PropCoef>(`${PC_BASE}/${id}/unpost`).then((r) => r.data),
  remove: (id: string) => api.delete(`${PC_BASE}/${id}`).then(() => undefined),
};
